/**
 * 滑块验证码组件
 *
 * 流程:
 * 1. 组件挂载时自动获取滑块验证
 * 2. 用户拖动滑块到拼图缺口位置
 * 3. 释放鼠标时提交验证
 * 4. 验证成功回调返回 captchaToken
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  generateSliderCaptcha,
  verifySliderCaptcha,
  type SliderGenerateResponse,
} from '../services/sliderApi';

interface SliderCaptchaProps {
  onSuccess: (captchaToken: string) => void;
  onClose: () => void;
}

const TRACK_WIDTH = 300;
const SLIDER_BTN_WIDTH = 40;

export const SliderCaptcha: React.FC<SliderCaptchaProps> = ({ onSuccess, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [sliderData, setSliderData] = useState<SliderGenerateResponse | null>(null);

  const [sliderLeft, setSliderLeft] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [track, setTrack] = useState<Array<{ x: number; t: number }>>([]);
  const [startTime, setStartTime] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);

  const loadCaptcha = useCallback(async () => {
    setLoading(true);
    setError('');
    setSliderLeft(0);
    try {
      const data = await generateSliderCaptcha();
      setSliderData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取验证码失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!sliderData || verifying) return;
      e.preventDefault();

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      setIsDragging(true);
      setDragStartX(clientX);
      setTrack([]);
      setStartTime(Date.now());
    },
    [sliderData, verifying]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDragging || !sliderData) return;
      e.preventDefault();

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const delta = clientX - dragStartX;
      const maxX = sliderData.background_width - SLIDER_BTN_WIDTH;
      const newLeft = Math.max(0, Math.min(delta, maxX));

      setSliderLeft(newLeft);
      setTrack((prev) => [...prev, { x: newLeft, t: Date.now() - startTime }]);
    },
    [isDragging, dragStartX, sliderData, startTime]
  );

  const handleMouseUp = useCallback(async () => {
    if (!isDragging || !sliderData) return;
    setIsDragging(false);

    if (sliderLeft < 10) return;

    setVerifying(true);
    try {
      const durationMs = Date.now() - startTime;
      const result = await verifySliderCaptcha(
        sliderData.token,
        sliderLeft,
        track,
        durationMs
      );
      onSuccess(result.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : '验证失败');
      setSliderLeft(0);
      setTimeout(() => {
        loadCaptcha();
      }, 1000);
    } finally {
      setVerifying(false);
    }
  }, [isDragging, sliderData, sliderLeft, track, startTime, onSuccess, loadCaptcha]);

  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMove = (e: MouseEvent | TouchEvent) => {
      handleMouseMove(e as any);
    };
    const handleGlobalUp = () => {
      handleMouseUp();
    };

    document.addEventListener('mousemove', handleGlobalMove);
    document.addEventListener('mouseup', handleGlobalUp);
    document.addEventListener('touchmove', handleGlobalMove);
    document.addEventListener('touchend', handleGlobalUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMove);
      document.removeEventListener('mouseup', handleGlobalUp);
      document.removeEventListener('touchmove', handleGlobalMove);
      document.removeEventListener('touchend', handleGlobalUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const sliderBgWidth = sliderData?.background_width || TRACK_WIDTH;
  const sliderBgHeight = sliderData?.background_height || 160;
  const sliderW = sliderData?.slider_width || 50;
  const sliderH = sliderData?.slider_height || 50;
  const puzzleY = sliderData?.puzzle_y ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div
        ref={containerRef}
        className="bg-white rounded-lg shadow-xl p-4 max-w-[360px] w-full mx-4"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-900">
            请完成安全验证
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center h-[160px] bg-gray-50 rounded">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-paper-500" />
          </div>
        )}

        {error && !loading && !sliderData && (
          <div className="flex flex-col items-center justify-center h-[160px] bg-gray-50 rounded">
            <p className="text-sm text-red-500 mb-2">{error}</p>
            <button
              onClick={loadCaptcha}
              className="text-sm text-paper-600 hover:text-paper-700"
            >
              重新获取
            </button>
          </div>
        )}

        {sliderData && !loading && (
          <>
            <div
              className="relative rounded overflow-hidden select-none"
              style={{
                width: sliderBgWidth,
                height: sliderBgHeight,
                margin: '0 auto',
              }}
            >
              <img
                src={sliderData.background_image}
                alt="验证背景"
                className="w-full h-full"
                draggable={false}
              />

              {sliderData.slider_image && (
                <img
                  src={sliderData.slider_image}
                  alt="滑块"
                  className="absolute"
                  style={{
                    left: sliderLeft,
                    top: puzzleY,
                    width: sliderW,
                    height: sliderH,
                    pointerEvents: 'none',
                  }}
                  draggable={false}
                />
              )}

              {verifying && (
                <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-paper-500" />
                </div>
              )}
            </div>

            <div
              className="relative mt-3 rounded-full h-[40px] bg-gray-100 border border-gray-200 select-none"
              style={{ width: sliderBgWidth, margin: '0 auto' }}
            >
              <div
                className="absolute top-0 left-0 h-full bg-paper-100 rounded-l-full"
                style={{ width: sliderLeft + SLIDER_BTN_WIDTH / 2 }}
              />

              <div
                className="absolute top-0 h-full flex items-center justify-center"
                style={{
                  left: sliderLeft,
                  width: SLIDER_BTN_WIDTH,
                }}
              >
                <div
                  className="w-[36px] h-[36px] bg-white border border-gray-300 rounded-full shadow-sm flex items-center justify-center cursor-grab active:cursor-grabbing hover:border-paper-400 hover:shadow-md transition-shadow"
                  onMouseDown={handleMouseDown}
                  onTouchStart={handleMouseDown}
                  style={{
                    opacity: verifying ? 0.5 : 1,
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M6 8L3 8M6 8L3 8M10 8L13 8M10 8L13 8M6 8L3 8M10 8L13 8"
                      stroke="#666"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M5 5L3 8L5 11M11 5L13 8L11 11"
                      stroke="#999"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              {!isDragging && sliderLeft === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-xs text-gray-400">
                    向右拖动滑块完成验证
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {error && sliderData && (
          <p className="text-xs text-red-500 mt-2 text-center">{error}</p>
        )}
      </div>
    </div>
  );
};

export default SliderCaptcha;
